import { zodResolver } from '@hookform/resolvers/zod';
import {
  type GoalRealismCheckResponse,
  GoalTypeSchema,
  type SubGoal,
  SubGoalSchema,
  type UpdateGoalInput,
} from '@onflow/shared';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { RealismCheckResult } from '../../components/RealismCheckResult.js';
import {
  useGoalRealismCheck,
  useOnboardingProfile,
  useUpdateGoal,
} from '../../hooks/useOnboarding.js';
import { toast } from '../../lib/toast.js';

const GOAL_OPTIONS = [
  { value: 'lose_weight', label: 'Lose Weight' },
  { value: 'gain_muscle', label: 'Gain Muscle' },
  { value: 'bulk', label: 'Bulk (Aggressive Muscle Gain)' },
  { value: 'maintain', label: 'Maintain / Recomp' },
  { value: 'other', label: 'Other Goals' },
] as const;

const SUB_GOAL_OPTIONS: ReadonlyArray<{ value: SubGoal; label: string }> = [
  { value: 'abs', label: 'Get Abs / Core Definition' },
  { value: 'belly_fat', label: 'Lose Belly Fat' },
  { value: 'stamina', label: 'Improve Stamina & Endurance' },
  { value: 'flexibility', label: 'Improve Flexibility & Mobility' },
  { value: 'athletic', label: 'Athletic Performance' },
  { value: 'postpartum', label: 'Postpartum Recovery' },
  { value: 'general_health', label: 'General Health & Longevity' },
];

// Sub-goals that don't imply a body-composition target — if all selected
// sub-goals are in this set, hide the target weight field.
const NO_TARGET_WEIGHT_SUBGOALS: ReadonlySet<SubGoal> = new Set(['stamina', 'flexibility']);

function todayPlusDays(n: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function dateInputToIso(yyyymmdd: string): string {
  // Date inputs are timezone-naive YYYY-MM-DD. Anchor at UTC midnight so the
  // backend's IsoDateSchema (z.string().datetime({offset:true})) accepts it.
  return new Date(`${yyyymmdd}T00:00:00.000Z`).toISOString();
}

function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}

const GoalFormSchema = z.object({
  goalType: GoalTypeSchema,
  subGoals: z.array(SubGoalSchema).default([]),
  currentWeightKg: z
    .number({ invalid_type_error: 'Enter your current weight' })
    .min(30, 'Must be at least 30 kg')
    .max(300, 'Must be at most 300 kg'),
  targetWeightKg: z
    .number({ invalid_type_error: 'Enter a target weight' })
    .min(30, 'Must be at least 30 kg')
    .max(300, 'Must be at most 300 kg')
    .nullable()
    .optional(),
  targetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick a target date'),
});

type GoalFormValues = z.infer<typeof GoalFormSchema>;

export function GoalLockScreen() {
  const navigate = useNavigate();
  const realismCheck = useGoalRealismCheck();
  const updateGoal = useUpdateGoal();
  const profile = useOnboardingProfile();

  const minDate = useMemo(() => todayPlusDays(7), []);
  const defaultDate = useMemo(() => todayPlusDays(60), []);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm<GoalFormValues>({
    resolver: zodResolver(GoalFormSchema),
    defaultValues: {
      goalType: profile.data?.goalType ?? undefined,
      subGoals: profile.data?.subGoals ?? [],
      currentWeightKg: profile.data?.currentWeightKg ?? undefined,
      targetWeightKg: profile.data?.targetWeightKg ?? null,
      targetDate: profile.data?.targetDate ? isoToDateInput(profile.data.targetDate) : defaultDate,
    },
  });

  const goalType = useWatch({ control, name: 'goalType' });
  const watchedSubGoals = useWatch({ control, name: 'subGoals' });
  const subGoals = useMemo(() => watchedSubGoals ?? [], [watchedSubGoals]);
  const targetDate = useWatch({ control, name: 'targetDate' });

  const showSubGoals = goalType === 'other';
  const hideTargetWeight = useMemo(() => {
    if (goalType === 'maintain') return true;
    if (goalType === 'other') {
      return subGoals.length > 0 && subGoals.every((sg) => NO_TARGET_WEIGHT_SUBGOALS.has(sg));
    }
    return false;
  }, [goalType, subGoals]);

  const [realismResult, setRealismResult] = useState<GoalRealismCheckResponse | null>(null);

  // Whenever the user changes the target date, the existing realism advisory
  // is stale. Clearing forces a fresh check on the next Next click.
  useEffect(() => {
    setRealismResult(null);
  }, [targetDate]);

  // Per spec: when the field is hidden, the form value should not carry a
  // leftover number into the submission. Reset to null on transition to hidden.
  useEffect(() => {
    if (hideTargetWeight) {
      setValue('targetWeightKg', null);
    }
  }, [hideTargetWeight, setValue]);

  function buildPayload(values: GoalFormValues): UpdateGoalInput {
    return {
      goalType: values.goalType,
      subGoals: values.goalType === 'other' ? (values.subGoals ?? []) : [],
      currentWeightKg: values.currentWeightKg,
      targetWeightKg: hideTargetWeight ? null : (values.targetWeightKg ?? null),
      targetDate: dateInputToIso(values.targetDate),
    };
  }

  async function proceedAndNavigate(payload: UpdateGoalInput) {
    try {
      await updateGoal.mutateAsync(payload);
      navigate('/onboarding/body-stats');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save goal');
    }
  }

  async function onValidSubmit(values: GoalFormValues) {
    const payload = buildPayload(values);

    try {
      const result = await realismCheck.mutateAsync({
        goalType: payload.goalType,
        currentWeightKg: payload.currentWeightKg,
        targetWeightKg: payload.targetWeightKg ?? undefined,
        targetDate: payload.targetDate,
      });

      if (result.classification === 'realistic') {
        await proceedAndNavigate(payload);
      } else {
        setRealismResult(result);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Goal check failed');
    }
  }

  function handleUseSuggestion() {
    if (!realismResult?.suggestedDate) return;
    setValue('targetDate', isoToDateInput(realismResult.suggestedDate), {
      shouldValidate: true,
      shouldDirty: true,
    });
    // The targetDate effect clears realismResult, so the next Next click
    // re-runs the check against the new date.
  }

  function handleKeepDate() {
    const values = getValues();
    void proceedAndNavigate(buildPayload(values));
  }

  const checking = realismCheck.isPending;
  const saving = updateGoal.isPending;
  const nextLabel = checking ? 'Checking…' : saving ? 'Saving…' : 'Next';

  function toggleSubGoal(value: SubGoal) {
    const next = subGoals.includes(value)
      ? subGoals.filter((sg) => sg !== value)
      : [...subGoals, value];
    setValue('subGoals', next, { shouldDirty: true });
  }

  return (
    <section className="onboarding-screen">
      <form
        className="onboarding-card onboarding-form"
        onSubmit={handleSubmit(onValidSubmit)}
        noValidate
      >
        <h2 className="onboarding-title">Set your goal</h2>
        <p className="onboarding-subtitle">Choose your primary fitness goal.</p>

        <fieldset className="form-group radio-group" aria-invalid={!!errors.goalType}>
          <legend className="form-legend">Goal</legend>
          {GOAL_OPTIONS.map((opt) => (
            <label key={opt.value} className="radio-option">
              <input type="radio" value={opt.value} {...register('goalType')} />
              <span>{opt.label}</span>
            </label>
          ))}
          {errors.goalType && (
            <span role="alert" className="form-error">
              Pick a goal to continue.
            </span>
          )}
        </fieldset>

        {showSubGoals && (
          <fieldset className="form-group checkbox-group">
            <legend className="form-legend">Select all that apply</legend>
            {SUB_GOAL_OPTIONS.map((opt) => (
              <label key={opt.value} className="checkbox-option">
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={subGoals.includes(opt.value)}
                  onChange={() => toggleSubGoal(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </fieldset>
        )}

        <div className="form-group">
          <label htmlFor="goal-current-weight">Current weight (kg)</label>
          <input
            id="goal-current-weight"
            type="number"
            min={30}
            max={300}
            step={0.1}
            inputMode="decimal"
            placeholder="80"
            aria-invalid={!!errors.currentWeightKg}
            {...register('currentWeightKg', { valueAsNumber: true })}
          />
          {errors.currentWeightKg && (
            <span role="alert" className="form-error">
              {errors.currentWeightKg.message}
            </span>
          )}
        </div>

        {!hideTargetWeight && (
          <div className="form-group">
            <label htmlFor="goal-target-weight">Target weight (kg)</label>
            <input
              id="goal-target-weight"
              type="number"
              min={30}
              max={300}
              step={0.1}
              inputMode="decimal"
              placeholder="75"
              aria-invalid={!!errors.targetWeightKg}
              {...register('targetWeightKg', { valueAsNumber: true })}
            />
            {errors.targetWeightKg && (
              <span role="alert" className="form-error">
                {errors.targetWeightKg.message}
              </span>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="goal-target-date">Target date</label>
          <input
            id="goal-target-date"
            type="date"
            min={minDate}
            aria-invalid={!!errors.targetDate}
            {...register('targetDate')}
          />
          {errors.targetDate && (
            <span role="alert" className="form-error">
              {errors.targetDate.message}
            </span>
          )}
        </div>

        {realismResult && realismResult.classification !== 'realistic' && (
          <RealismCheckResult
            classification={realismResult.classification}
            message={realismResult.message}
            suggestedDate={realismResult.suggestedDate ?? null}
            onUseSuggestion={handleUseSuggestion}
            onKeepDate={handleKeepDate}
          />
        )}

        {checking && <p className="form-status">Checking your goal…</p>}

        <div className="onboarding-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/onboarding/mode')}
          >
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={checking || saving}>
            {nextLabel}
          </button>
        </div>
      </form>
    </section>
  );
}
